import { Severity as SeverityProto } from "@protos/common/v1/common";
import { Issue, IssueType } from "@protos/issues/v1/issues";
import { Rule } from "@shared/types";

/**
 * Deprecated -- use the `Severity` proto enum.
 */
export type SeverityStr = "Low" | "Medium" | "High";

/**
 * Deprecated -- use the `Severity` proto enum.
 */
export const severities: SeverityStr[] = ["Low", "Medium", "High"];

export const getSeverityProtoFromIssue = (issue: Issue) => {
  if (issue.issueType === IssueType.SCA) {
    return issue.scaAttributes?.severity ?? SeverityProto.UNSPECIFIED;
  }
  return issue.severity;
};

export const getSeverityProtoFromRule = (
  rule: Rule,
  issueType: IssueType
): SeverityProto => {
  // SCA Severity comes from the backend as LOW, MEDIUM, HIGH, or CRITICAL
  // while other products' Severity comes from the backend as INFO, WARNING or ERROR.
  let ruleSeverity = rule.definition.rules[0].severity;
  if (issueType === IssueType.SCA) {
    ruleSeverity = rule.definition.rules[0].metadata?.["sca-severity"] ?? "";
  }
  return ruleSeverityStrToSeverityProto(ruleSeverity);
};

/**
 * Returns the severity to display for a given issue.
 *
 * If the issue is a SAST or SCA issue, the severity is taken from the rule.
 * If the issue is a SECRETS issue, the severity is taken from the issue.
 *
 * @param rule Rule to grab severity from
 * @param issue Issue to grab severity from
 */
export const getDisplaySeverity = (rule: Rule, issue: Issue): SeverityProto => {
  switch (issue.issueType) {
    case IssueType.SAST:
    case IssueType.SCA:
      return getSeverityProtoFromRule(rule, issue.issueType);
    case IssueType.SECRETS:
      // For secrets we cannot rely on the rule's severity since a validator can override it.
      return getSeverityProtoFromIssue(issue);
    default:
      return SeverityProto.UNSPECIFIED;
  }
};

export const ruleSeverityStrToSeverityProto = (
  ruleSeverityStr: string
): SeverityProto => {
  switch (ruleSeverityStr) {
    case "INFO":
    case "LOW":
      return SeverityProto.LOW;
    case "WARNING":
    case "MEDIUM":
    case "MODERATE":
      return SeverityProto.MEDIUM;
    case "ERROR":
    case "HIGH":
      return SeverityProto.HIGH;
    case "CRITICAL":
      return SeverityProto.CRITICAL;
    default:
      return SeverityProto.UNSPECIFIED;
  }
};

/*
 * TODO: remove this once protos are more widely adopted.
 */
export const severityStrToProto = (severityStr: string): SeverityProto => {
  switch (severityStr) {
    case "Low":
      return SeverityProto.LOW;
    case "Medium":
      return SeverityProto.MEDIUM;
    case "High":
      return SeverityProto.HIGH;
    case "Critical":
      return SeverityProto.CRITICAL;
  }

  return SeverityProto.UNSPECIFIED;
};

/**
 * @description This function is used to sort the rules by severity (lower number is higher priority)
 * @param severity - the severity of the finding
 * @returns a number representing the priority of the severity
 *
 */
export const severityToPriority = (severity: SeverityStr) => {
  switch (severity) {
    case "High":
      return 1;
    case "Medium":
      return 2;
    case "Low":
      return 3;
    default:
      return 4;
  }
};

export const severityToLabel = (severity: SeverityProto): string => {
  switch (severity) {
    case SeverityProto.MEDIUM:
      return "Medium";
    case SeverityProto.HIGH:
      return "High";
    case SeverityProto.CRITICAL:
      return "Critical";
    case SeverityProto.UNSPECIFIED:
    case SeverityProto.LOW:
    default:
      return "Low";
  }
};

/**
 * Returns the severity of the rule group based on the issue type.
 *
 * @param issue - the issue to get the severity from
 * @param ruleSeverity - the severity of the rule
 */
export const getRuleGroupSeverity = (
  issue: Issue,
  ruleSeverity: SeverityProto
): SeverityProto => {
  if (issue.issueType === IssueType.SECRETS) {
    return getSeverityProtoFromIssue(issue);
  }

  return ruleSeverity;
};
